import React, { useEffect, useState, useRef, useCallback } from "react";
import Card from "../../components/card/card";
import { useParams } from 'react-router-dom';
import { connect } from "react-redux";
import moment from 'moment';
import "./spaces.css";
import { AnimatePresence, motion } from 'framer-motion';

function Spaces({ contents = {}, searchQuery = '' }) {
    const { timeline } = useParams();
    const loaderRef = useRef(null);
    const [allFilteredItems, setAllFilteredItems] = useState([]);
    const [visibleItems, setVisibleItems] = useState([]);
    const itemsPerBatch = 12;
    const [visibleCount, setVisibleCount] = useState(itemsPerBatch);
    const [loading, setLoading] = useState(false);

    const formattedDate = moment(new Date());
    const _baseClass = "spaces";

    // Memoized filter logic
    const getFilteredItems = useCallback(() => {
        let filtered = timeline
            ? (contents?.items || []).filter((n) => {
                const createdAt = moment(n.sys?.createdAt);
                return timeline === createdAt.format("MMM-YYYY").toLowerCase();
            })
            : contents?.items || [];

        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            filtered = filtered.filter((item) => {
                const titleMatch = item.fields?.postTitle?.toLowerCase().includes(lowerQuery);
                const departmentMatch = Array.isArray(item.fields?.postDepartmentList)
                    ? item.fields.postDepartmentList.some(dept => dept.toLowerCase().includes(lowerQuery))
                    : false;
                const contentMatch = item.fields?.postContent?.toLowerCase().includes(lowerQuery);
                return titleMatch || departmentMatch || contentMatch;
            });
        }

        return filtered;
    }, [timeline, contents, searchQuery]);

    // Update filtered list and visible items when dependencies change
    useEffect(() => {
        const filtered = getFilteredItems();
        setAllFilteredItems(filtered);
        setVisibleItems(filtered.slice(0, visibleCount));
    }, [getFilteredItems, visibleCount]);

    // Infinite scroll via IntersectionObserver
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting && !loading && visibleCount < allFilteredItems.length) {
                setLoading(true);
                setTimeout(() => {
                    setVisibleCount(prev => prev + itemsPerBatch);
                    setLoading(false);
                }, 300); // simulate delay
            }
        }, {
            root: null,
            rootMargin: "0px",
            threshold: 0.1
        });

        if (loaderRef.current) observer.observe(loaderRef.current);

        return () => {
            if (loaderRef.current) observer.unobserve(loaderRef.current);
        };
    }, [visibleCount, allFilteredItems.length, loading]);

    return (
        <div className={_baseClass + " m-[17.5px] sm:m-0"}>
            <div className="w-full flex flex-col gap-[10px]">
                <div className="w-full flex flex-row">
                    <div className="w-[50%] flex h-0 overflow-hidden sm:h-full sm:overflow-visible">
                        <div className="font-sofia-sb text-[45px] text-[#1C355E] font-black">SPACES</div>
                    </div>
                    <div className="w-[50%] flex flex-col justify-center h-0 overflow-hidden sm:h-full sm:overflow-visible">
                        <div className="w-full text-right text-[#84818A] text-[16px] font-medium">{formattedDate.format('dddd')}</div>
                        <div className="w-full text-right text-[#2E2C34] text-[14px] font-semibold">{formattedDate.format('D MMMM YYYY')}</div>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-[10px] sm:gap-[30px] pb-4">
                    {visibleItems.map((news, index) => {
                        const cardStyle =
                            index < 1
                                ? "col-span-12 md:col-span-6 initial-card h-[400px] sm:h-[470px]"
                                : index < 2
                                    ? " col-span-6 md:col-span-6 h-[270px] sm:h-[470px]"
                                    : index < 5
                                        ? " col-span-6 md:col-span-6 xl:col-span-4 h-[270px] sm:h-[470px]"
                                        : " col-span-6 md:col-span-6 xl:col-span-3 h-[270px] sm:h-[470px]";

                        return (
                            <div key={index} className={"transition-all duration-300 hover:shadow-md hover:cursor-pointer rounded-[16px] grid-cols-subgrid " + cardStyle}>
                                <AnimatePresence>
                                    <motion.div
                                        className="h-full w-full"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 1.5, ease: 'easeInOut' }}
                                    >
                                        <Card data={news} index={index} />
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>

                {/* Loader */}
                {visibleCount < allFilteredItems.length && (
                    <div ref={loaderRef} className="flex justify-center py-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
                    </div>
                )}
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    contents: state.contents,
    searchQuery: state.search.query,
});

export default connect(mapStateToProps)(Spaces);
